import {
  TIME_SLOTS,
  WORK_STEPS,
  assigneeInSlot,
  CHECK_IN_THE_MAIL_DAYS,
  HANDOFF_PAY_METHODS,
  capitalise,
  handoffAsksAboutCheckReminders,
  handoffDefaults,
  handoffEmail,
  hiddenChecklistLabels,
  inspectionSlots,
  inspectorOptions,
  isMountainBrook,
  nextItemStatus,
  opensWarrantyForm,
  tradeProgress,
  workStepsAreManual,
  type BidType,
  type HandoffAnswers,
  type HandoffFacts,
  type HandoffPayMethod,
  type Job,
  type JobChecklistItem,
  type Profile,
  type RoofLayout,
  type WorkOrder,
} from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import {
  InspectionFields,
  emptyInspectionDraft,
  inspectionAssigneeColumns,
  inspectionIso,
  type InspectionDraft,
} from "../../components/InspectionFields";
import { CHECK_ICON, Modal, PhoneInput } from "../../components/ui";
import { t } from "../../i18n";
import {
  fmtDateOnly,
  fmtDateTime,
  toLocalDateTimeInput,
} from "../../lib/format";
import {
  bookInspection,
  calendarNote as noteForCalendar,
} from "../../lib/inspection";
import { closeJob, reopenJob, type CloseoutRow } from "../../lib/closeout";
import { fetchHandoffFacts } from "../../lib/handoff";
import { recordInvoiceAmount } from "../../lib/queries";
import { fetchJobWarranty, fetchRegisterFacts } from "../../lib/warranties";
import { fetchSendsEmail } from "../../lib/selections";
import { unwindSale } from "../../lib/unwindSale";
import { useSession } from "../../session";
import { ComposeEmail, type EmailDraft } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";
import { MeasureRoofModal } from "./MeasureRoofModal";
import { NightBeforeModal } from "./NightBeforeModal";
import { PermitModal } from "./permit/PermitModal";
import { RegisterWarrantyModal } from "./RegisterWarrantyModal";
import { SendToCloseoutModal } from "./SendToCloseoutModal";
import {
  RoofMeasurementModal,
  type RoofLayoutInput,
} from "./RoofMeasurementModal";

/**
 * The two steps the work orders decide, rather than a person.
 *
 * They replaced "Roof Scheduled" / "Work Orders Scheduled" and "Roof Complete"
 * / "Trades Complete", which assumed every job was a roof. A gutters-only job
 * now reads the same as a roof job, and a roof-plus-trades job shows each
 * trade's own state underneath.
 *
 * Unless the job is handled in-house: a job with `work_orders_internal` set
 * raises no work orders at all, so these two go back to being ticked by hand
 * (ADR 0102). `WORK_STEPS` is the same pair, named in the domain.
 */
const isWorkStep = (label: string): boolean =>
  (WORK_STEPS as readonly string[]).includes(label);

type WorkOrderLike = Pick<
  WorkOrder,
  "id" | "trades" | "status" | "scheduled_at" | "start_at" | "completed_at"
>;

export interface VerifyValues {
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  cell: string | null;
  phone: string | null;
  phoneLabel: string | null;
  email: string | null;
  leadSource: string;
  bidType: BidType;
  claimNumber: string | null;
  note: string | null;
}

export function ChecklistPanel({
  job,
  items,
  onSetStatus,
  onPatchJob,
  onVerify,
  onOpenEstimate,
  layouts,
  onSaveLayout,
  onDeleteLayout,
  onSaveContract,
  onRpc,
  onRefresh,
  onQuickMeasureOrdered,
  onCheckComing,
  workOrders,
  onOpenWorkOrders,
  closeout = null,
  closeoutOwner = null,
}: {
  job: Job;
  items: readonly JobChecklistItem[];
  onSetStatus: (
    item: JobChecklistItem,
    status: "pending" | "done" | "na",
  ) => Promise<void>;
  /** Resolves false when the save failed; the page shows why. */
  onPatchJob: (patch: Partial<Job>) => Promise<boolean | void>;
  onVerify: (item: JobChecklistItem, values: VerifyValues) => Promise<void>;
  /** Jump to the estimate card, creating one if the job has none. */
  onOpenEstimate: () => void;
  layouts: readonly RoofLayout[];
  onSaveLayout: (input: RoofLayoutInput) => Promise<void>;
  onDeleteLayout: (layout: RoofLayout) => Promise<void>;
  onSaveContract: (input: SignedContractInput) => Promise<void>;
  onRpc: (fn: string, args: Record<string, unknown>) => Promise<void>;
  onRefresh: () => Promise<void>;
  /**
   * The rep was sent to QuickMeasure to order a report.
   *
   * Recorded on the job, because the material order email asks Heely-Brown to
   * credit the report and can only ask if something wrote down that one was
   * ordered (ADR 0110).
   */
  onQuickMeasureOrdered?: () => Promise<void>;
  /**
   * The rep says the customer is paying by check, and the payment reminders
   * on the job wait for it (ADR 0263). The handoff asks (ADR 0301).
   */
  onCheckComing?: (coming: boolean) => Promise<void>;
  /** Every work order on the job — these decide the two work steps. */
  workOrders: readonly WorkOrderLike[];
  /** Where "All Work Scheduled" and "All Work Complete" send you. */
  /**
   * Leaves for the work orders, on the order named where one is named.
   *
   * The two work steps are not ticked here — the work orders decide them, and
   * the box that moves is "Mark As Complete" inside a work order. Without an
   * id this used to land on the top of the estimate page and leave somebody to
   * find that themselves, which is a step that looks broken.
   */
  onOpenWorkOrders: (orderId?: string) => void;
  /** The job's send to Kelsey, once it has been sent (ADR 0273). */
  closeout?: CloseoutRow | null;
  /** Who finished jobs go to. Null when nobody is set. */
  closeoutOwner?: Profile | null;
}) {
  const ownerFirst = closeoutOwner?.full_name?.split(" ")[0] ?? "Kelsey";
  const [modal, setModal] = useState<
    | {
        kind:
          | "verify"
          | "inspection"
          | "handoff"
          | "measurement"
          | "draw"
          | "contract"
          | "unwind"
          | "permit"
          | "warranty";
        item: JobChecklistItem;
      }
    | { kind: "reassign" }
    | { kind: "closeout" }
    | { kind: "sendsEmail" }
    | { kind: "nightBefore" }
    | null
  >(null);

  /**
   * What the calendar did with the last inspection saved from here.
   *
   * Null most of the time. A calendar that refused the appointment, or an
   * assignee with no email address, is worth one line on the screen the moment
   * it happens — `jobs.inspection_event_error` is what carries a refusal past
   * this render, but somebody standing in front of a customer should not have
   * to reload to find out.
   */
  const [calendarNote, setCalendarNote] = useState<string | null>(null);

  /** What sending to Kelsey, or closing, has to say — a refusal, a failed email. */
  const [closeoutNote, setCloseoutNote] = useState<string | null>(null);

  /**
   * A handoff whose email could not be kept on the job (ADR 0304). The
   * handoff itself went, so it is not undone; this says so after the step
   * ticks, which clears the page's own error, and points at the way back in.
   */
  const [sendsEmailNote, setSendsEmailNote] = useState<string | null>(null);

  /**
   * The steps in one phase, minus anything the in-house switches take off the
   * list. The hidden rows are still there in the database, marked N/A, so the
   * ongoing-jobs board is not left waiting on a step nobody can see.
   */
  const hidden = hiddenChecklistLabels(job);
  const phase = (name: string) =>
    items.filter((i) => i.phase === name && !hidden.includes(i.label));
  /** The job has been handed to production, so there is an approved list. */
  const approved = Boolean(job.sends_at);
  /** No work orders are coming, so a person ticks the two work steps. */
  const manualWork = workStepsAreManual(job);

  /**
   * Turn one of the in-house switches on or off.
   *
   * Refetched rather than patched in place: flipping either one moves checklist
   * statuses by database trigger — "Material" to N/A and back, and the two work
   * steps back into the work orders' hands — so the list on the screen is out
   * of date the moment the switch lands.
   */
  async function flipInternal(
    column: "materials_internal" | "material_by_crew" | "work_orders_internal",
  ) {
    await onPatchJob({ [column]: !job[column] } as Partial<Job>);
    await onRefresh();
  }

  async function clickItem(item: JobChecklistItem) {
    if (item.label === "Sends") {
      if (item.status !== "done") {
        setModal({ kind: "handoff", item });
      } else {
        if (
          !window.confirm(
            t("Undo the handoff? This clears the approved checklist."),
          )
        )
          return;
        await onRpc("unsend_job", { p_job: job.id });
        await onSetStatus(item, "pending");
        await onRefresh();
      }
      return;
    }
    if (item.label === "Verified" && item.status !== "done") {
      setModal({ kind: "verify", item });
      return;
    }
    // Estimate hands over to the estimate card, which owns creating one. The
    // step ticks itself when an estimate exists, so there is nothing to toggle.
    if (item.label === "Estimate") {
      onOpenEstimate();
      return;
    }
    if (item.label === "Signed Contract" && item.status !== "done") {
      setModal({ kind: "contract", item });
      return;
    }
    // Unticking a signature unwinds the sale, everywhere it is counted
    // (ADR 0326; Jonathon, 2026-10-31, after Dennis Burris's financing fell
    // through). A plain flip left the signed date, the approved estimate and
    // the deposit request behind, so the sale went on counting. This screen
    // is the only way in for now: MCP `set_checklist_item` still flips the
    // box alone, until #1122 routes it through `unwind_sale` or refuses it.
    if (item.label === "Signed Contract") {
      setModal({ kind: "unwind", item });
      return;
    }
    // "Estimate Sent" ticks itself the moment a document is presented — the
    // one write behind Copy link, Email it and Sign on this device
    // (20260919100000, ADR 0164). It is still a plain toggle here on purpose:
    // a rep who sent an estimate from their own mail client has to be able to
    // say so, and one who presented the wrong document has to be able to take
    // it back.
    if (item.label === "Inspection Scheduled" && item.status !== "done") {
      setModal({ kind: "inspection", item });
      return;
    }
    // Getting the roof measured: draw it in iRoofing or order a QuickMeasure
    // report. Worth opening after it is ticked too — a second roof, or a report
    // that never arrived and has to be ordered again (ADR 0109).
    if (item.label === "Draw Roof") {
      setModal({ kind: "draw", item });
      return;
    }
    // Measurements are worth opening even once the step is ticked — a second
    // layout often turns up after the first visit.
    if (item.label === "Roof Measurement") {
      setModal({ kind: "measurement", item });
      return;
    }
    // The two work steps are not ticked by hand — the work orders decide them,
    // and a job can be a roof, a roof plus other trades, or no roof at all.
    // Clicking opens the work orders rather than doing nothing. On a job
    // handled in-house there are no work orders to open, so they flip like any
    // other step (ADR 0102).
    if (isWorkStep(item.label) && !manualWork) {
      // Straight into the order where there is only one, which is most jobs —
      // a roof and nothing else. With several it opens the list, and the
      // strip under the step is how somebody picks the trade they mean.
      onOpenWorkOrders(
        workOrders.length === 1 ? (workOrders[0]?.id ?? undefined) : undefined,
      );
      return;
    }
    // Closed moves the stage, so it goes through the same write Kelsey's
    // board uses. Anyone on staff may tick it (ADR 0273; Jonathon,
    // 2026-10-12: "anyone can close them").
    if (
      item.label === "Closed" &&
      (item.phase === "production" || item.phase === "followup")
    ) {
      try {
        if (item.status === "done") await reopenJob(job.id);
        else await closeJob(job.id);
        setCloseoutNote(null);
      } catch (e) {
        setCloseoutNote(e instanceof Error ? e.message : String(e));
      }
      await onRefresh();
      return;
    }
    // A Mountain Brook permit: the city's pink form, filled from the job, to
    // save to Docs and upload on the city's own form (ADR 0289). Opened
    // whatever its state; the prompt ticks it. Any other city flips as before.
    if (item.label === "Permit" && isMountainBrook(job)) {
      setModal({ kind: "permit", item });
      return;
    }
    // The manufacturer's warranty: on a TAMKO or GAF roof, a form whatever
    // the step's state, so a registered one can be corrected and an N/A one
    // explained; the row written there moves the step (ADR 0309). Gutters,
    // siding or another manufacturer's warranty flip with one press below, as
    // they always did (Jonathon, 2026-10-27). A read that fails opens the
    // form, which says what it could not load.
    if (item.label === "Register Warranty") {
      const roof = await Promise.all([
        fetchJobWarranty(job.id),
        fetchRegisterFacts(job.id),
      ]).then(
        ([record, facts]) => ({
          recorded: record !== null,
          manufacturer: facts.manufacturer,
        }),
        () => null,
      );
      if (!roof || opensWarrantyForm(roof)) {
        setModal({ kind: "warranty", item });
        return;
      }
    }
    // Everything else: flip it and leave the page alone.
    await onSetStatus(item, nextItemStatus(item));
  }

  /**
   * One step.
   *
   * A ticked step carries when it was ticked, in place. It is the record of
   * what happened and when — the same `completed_at` the reports read — so it
   * belongs where the person doing the work can see it, not only in Activity.
   */
  const renderItem = (item: JobChecklistItem) => {
    const auto = isWorkStep(item.label) && !manualWork;
    return (
      <div key={item.id}>
        <div
          className={`item ${item.status}${auto ? " auto" : ""}`}
          onClick={() => void clickItem(item)}
        >
          <span className="mark">
            {item.status === "na" ? t("N/A") : CHECK_ICON}
          </span>
          <span className="label">{t(item.label)}</span>
          {item.completed_at && (
            <span className="when">{fmtDateTime(item.completed_at)}</span>
          )}
          {/* The handoff email, one tap from the step that sent it (ADR
              0304). Its own button, so opening it never undoes the handoff. */}
          {item.label === "Sends" &&
            (item.status === "done" || job.sends_email) && (
              <button
                type="button"
                className="item-side"
                onClick={(e) => {
                  e.stopPropagation();
                  setModal({ kind: "sendsEmail" });
                }}
              >
                {t("Sends email")}
              </button>
            )}
        </div>
        {auto && (
          <TradeStrip
            label={item.label}
            orders={workOrders}
            onOpen={onOpenWorkOrders}
          />
        )}
        {/* The night-before email, right under scheduling the work: the
            evening before the crew arrives is when it is sent, and the booked
            day is what it reads from (ADR 0313). */}
        {item.phase === "production" && item.label === "All Work Scheduled" && (
          <button
            type="button"
            className="reassign night-before"
            onClick={() => setModal({ kind: "nightBefore" })}
          >
            {t("✉ Night-before email to the customer")}
          </button>
        )}
        {item.phase === "production" &&
          item.label === "Final Check" &&
          item.status === "done" &&
          renderCloseout()}
      </div>
    );
  };

  /**
   * Send to Kelsey, under Final Check once it is ticked (ADR 0273).
   *
   * After Final Check and not before: that is when the last of the money is
   * in, and his words were "after the final check complete". Once sent, the
   * button gives way to who sent it and when.
   */
  const renderCloseout = () => (
    <CloseoutStep
      closeout={closeout}
      ownerFirst={closeoutOwner ? ownerFirst : null}
      onSend={() => setModal({ kind: "closeout" })}
    />
  );

  return (
    <>
      {/* What the calendar did, said once, where the person who just saved the
          inspection is looking. A refusal also survives on the job itself. */}
      {calendarNote && (
        <div className="config-warn" role="status">
          {calendarNote}{" "}
          <button className="btn-ghost" onClick={() => setCalendarNote(null)}>
            {t("Dismiss")}
          </button>
        </div>
      )}
      {/* The last attempt to reach a calendar, if it failed. Written by
          calendar-sync onto the job, so it outlives the tab that caused it —
          an appointment nobody knows is missing is the whole thing this
          feature exists to prevent (ADR 0040). */}
      {closeoutNote && (
        <div className="config-warn" role="status">
          {closeoutNote}{" "}
          <button className="btn-ghost" onClick={() => setCloseoutNote(null)}>
            {t("Dismiss")}
          </button>
        </div>
      )}
      {sendsEmailNote && (
        <div className="config-warn" role="status">
          {sendsEmailNote}{" "}
          <button className="btn-ghost" onClick={() => setSendsEmailNote(null)}>
            {t("Dismiss")}
          </button>
        </div>
      )}
      {job.inspection_event_error && (
        <div className="config-warn" role="status">
          {/* Not "is not on a calendar": a cancel Google refused leaves the
              event on one, and the message below says which it is. */}
          <strong>
            {t("Google Calendar is out of step with this inspection.")}
          </strong>{" "}
          {job.inspection_event_error}{" "}
          {t("Re-save the inspection to try again.")}
        </div>
      )}
      {/* Prospect on the left, always. The approved list joins it on the right
          the moment the job is handed to production — before that there is
          nothing to show there, and an empty column is just clutter. */}
      <div className={approved ? "chk-cols" : "chk-one"}>
        <div className="block">
          <h2>
            {t("Prospect Checklist")}{" "}
            <span className="na-hint">{t(capitalise(job.bid_type))}</span>
          </h2>
          <button
            className="reassign"
            onClick={() => setModal({ kind: "reassign" })}
          >
            {t("↻ Reassign Sales Rep")}
          </button>
          {phase("prospect").map(renderItem)}
        </div>

        {approved && (
          <div className="block">
            <h2>{t("Approved Checklist")}</h2>
            {/* What this job does not need ordering. Off on every count is
                the normal job; any switch comes straight back off, and a step
                that was already ticked keeps its tick. */}
            <div className="chk-internal">
              <button
                className={`internal${job.materials_internal ? " on" : ""}`}
                aria-pressed={Boolean(job.materials_internal)}
                onClick={() => void flipInternal("materials_internal")}
              >
                {job.materials_internal ? CHECK_ICON : ""}{" "}
                {t("Materials Handled Internally")}
              </button>
              <button
                className={`internal${job.material_by_crew ? " on" : ""}`}
                aria-pressed={Boolean(job.material_by_crew)}
                onClick={() => void flipInternal("material_by_crew")}
              >
                {job.material_by_crew ? CHECK_ICON : ""}{" "}
                {t("Crew Is Providing Material")}
              </button>
              <button
                className={`internal${job.work_orders_internal ? " on" : ""}`}
                aria-pressed={Boolean(job.work_orders_internal)}
                onClick={() => void flipInternal("work_orders_internal")}
              >
                {job.work_orders_internal ? CHECK_ICON : ""}{" "}
                {t("Work Orders Handled Internally")}
              </button>
            </div>
            {job.materials_internal && (
              <div className="na-hint chk-internal-note">
                {t(
                  "Material came off our own shelf — the Material step is not on this list.",
                )}
              </div>
            )}
            {job.material_by_crew && (
              <div className="na-hint chk-internal-note">
                {t(
                  "The crew is bringing the material — the Material step is not on this list.",
                )}
              </div>
            )}
            {job.work_orders_internal && (
              <div className="na-hint chk-internal-note">
                {t(
                  "No work orders on this job — tick the two work steps yourself when the work is booked and when it is finished.",
                )}
              </div>
            )}
            {phase("production").length > 0 && (
              <>
                <div className="chk-sub">{t("Production")}</div>
                {phase("production").map(renderItem)}
              </>
            )}
            {phase("followup").length > 0 && (
              <>
                <div className="chk-sub">{t("Follow-up")}</div>
                {phase("followup").map(renderItem)}
              </>
            )}
            {phase("financing").length > 0 && (
              <>
                <div className="chk-sub">{t("Financing")}</div>
                {phase("financing").map(renderItem)}
              </>
            )}
          </div>
        )}
      </div>

      {modal?.kind === "nightBefore" && (
        <NightBeforeModal
          job={job}
          workOrders={workOrders}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.kind === "warranty" && (
        <RegisterWarrantyModal
          job={job}
          item={modal.item}
          onClose={() => setModal(null)}
          onSaved={onRefresh}
        />
      )}

      {modal?.kind === "permit" && (
        <PermitModal
          job={job}
          item={modal.item}
          onClose={() => setModal(null)}
          onSetStatus={async (status) => {
            await onSetStatus(modal.item, status);
            setModal(null);
          }}
        />
      )}

      {modal?.kind === "closeout" && closeoutOwner && (
        <SendToCloseoutModal
          job={job}
          items={items}
          owner={closeoutOwner}
          onClose={() => setModal(null)}
          onSent={async (note) => {
            setModal(null);
            setCloseoutNote(note);
            await onRefresh();
          }}
        />
      )}

      {modal?.kind === "sendsEmail" && (
        <SendsEmailModal
          job={job}
          onClose={() => setModal(null)}
          onSave={async (email) =>
            (await onPatchJob({ sends_email: email })) !== false
          }
        />
      )}

      {modal?.kind === "reassign" && (
        <ReassignModal
          job={job}
          onClose={() => setModal(null)}
          onSave={async (repId) => {
            await onPatchJob({ rep_id: repId });
            setModal(null);
          }}
        />
      )}

      {modal?.kind === "verify" && (
        <VerifyModal
          job={job}
          onClose={() => setModal(null)}
          onSubmit={async (values) => {
            await onVerify(modal.item, values);
            setModal(null);
          }}
        />
      )}

      {modal?.kind === "measurement" && (
        <RoofMeasurementModal
          job={job}
          layouts={layouts}
          onClose={() => setModal(null)}
          onDelete={onDeleteLayout}
          onSave={async (input) => {
            await onSaveLayout(input);
            await onSetStatus(modal.item, "done");
            setModal(null);
          }}
        />
      )}

      {/* Draw Roof ticks when the rep is actually handed over to a service —
          pressing the button that copies the address and opens it. Opening the
          modal and closing it again says nothing happened, and leaves the step
          where it was. Nothing here can know whether the drawing was finished;
          the measurement arriving on Roof Measurement is what shows that. */}
      {modal?.kind === "draw" && (
        <MeasureRoofModal
          job={job}
          onClose={() => setModal(null)}
          onHandoff={(service) => {
            const item = modal.item;
            if (item.status !== "done") void onSetStatus(item, "done");
            if (service === "quickmeasure") void onQuickMeasureOrdered?.();
          }}
        />
      )}

      {modal?.kind === "contract" && (
        <SignedContractModal
          job={job}
          onClose={() => setModal(null)}
          onSave={async (input) => {
            await onSaveContract(input);
            await onSetStatus(modal.item, "done");
            setModal(null);
          }}
        />
      )}

      {modal?.kind === "unwind" && (
        <UnwindSaleModal
          onClose={() => setModal(null)}
          onUnwind={async (reason) => {
            await unwindSale(job.id, reason);
            setModal(null);
            await onRefresh();
          }}
        />
      )}

      {modal?.kind === "inspection" && (
        <InspectionModal
          job={job}
          onClose={() => setModal(null)}
          onSave={async (values) => {
            await onPatchJob({
              ...values.assignees,
              inspection_at: values.iso,
              inspection_notes: values.notes,
            });
            /*
             * Tick the step, then onto a real calendar (ADR 0040) — the one
             * sequence New Lead runs too (lib/inspection.ts).
             *
             * Saving is what schedules it — there is no pop-up and nothing to
             * press in another tab. It runs after the job is written, and a
             * failure here never undoes that: the inspection is scheduled
             * either way and the Cal tab has it, so a Google outage must not
             * cost somebody the appointment.
             *
             * But it is said out loud. The failure mode this whole feature
             * exists to prevent is an appointment nobody knows is missing, and
             * a result nobody looks at is exactly that — so the reason is shown
             * here, and `jobs.inspection_event_error` keeps it on the Overview
             * after this modal has closed.
             */
            const booking = await bookInspection({
              jobId: job.id,
              scheduled: values.iso !== null,
              minutes: values.durationMinutes,
              tick: () => onSetStatus(modal.item, "done"),
            });
            setCalendarNote(noteForCalendar(booking.calendar));

            // A tick that failed has already put the box back and shown why
            // (JobDetail); the modal stays, as it always did, for another go.
            if (booking.tickError) return;
            setModal(null);
            await onRefresh();
          }}
        />
      )}

      {modal?.kind === "handoff" && (
        <HandoffModal
          job={job}
          onClose={() => setModal(null)}
          onCheckComing={onCheckComing}
          onSent={async (email) => {
            await onRpc("send_job", { p_job: job.id });
            const kept =
              !email || (await onPatchJob({ sends_email: email })) !== false;
            await onSetStatus(modal.item, "done");
            setSendsEmailNote(
              kept
                ? null
                : t(
                    "The handoff went, but its email was not kept on the job. Paste it in with the Sends email button beside the Sends step.",
                  ),
            );
            // The job is sold, so accounting starts (docs/adr/0038). The
            // trigger has already queued the invoice; this fills in what it
            // is for, from the same ladder the estimate card was showing.
            //
            // ...and sends it to QuickBooks, without anybody pressing a
            // second button on a second screen (docs/adr/0042).
            //
            // Deliberately not awaited into the handoff's success: the handoff
            // has happened either way, and a QuickBooks hiccup must not leave a
            // rep staring at an error on a step that worked. A push that fails
            // leaves the row queued, and Tools → Integrations lists it.
            void recordInvoiceAmount(job).catch(() => {});
            setModal(null);
            await onRefresh();
          }}
        />
      )}
    </>
  );
}

/** The button under Final Check, or who sent the job and when. */
function CloseoutStep({
  closeout,
  ownerFirst,
  onSend,
}: {
  closeout: CloseoutRow | null;
  /** The close-out person's first name; null when nobody is set. */
  ownerFirst: string | null;
  onSend: () => void;
}) {
  const { people } = useSession();
  if (closeout) {
    const by =
      people.find((p) => p.id === closeout.sent_by)?.full_name ?? t("someone");
    return (
      <div className="na-hint closeout-sent">
        {t("Sent to {owner} by {by} on {when}", {
          owner: ownerFirst ?? t("close"),
          by,
          when: fmtDateTime(closeout.sent_at),
        })}
      </div>
    );
  }
  if (!ownerFirst) {
    return (
      <div className="na-hint closeout-sent">
        {t(
          "Nobody is set to receive finished jobs. An admin sets it in Tools → Notifications.",
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="btn-primary closeout-send"
      onClick={onSend}
    >
      {t("Send to {name}", { name: ownerFirst })}
    </button>
  );
}

function ReassignModal({
  job,
  onClose,
  onSave,
}: {
  job: Job;
  onClose: () => void;
  onSave: (repId: string | null) => Promise<void>;
}) {
  const { people } = useSession();
  const [repId, setRepId] = useState(job.rep_id ?? "");

  return (
    <Modal
      title={t("Reassign sales rep")}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            onClick={() => void onSave(repId || null)}
          >
            {t("Save")}
          </button>
        </>
      }
    >
      <select value={repId} onChange={(e) => setRepId(e.target.value)}>
        <option value="">—</option>
        {/* Reps who are still here. `people` carries everybody so that a job
            can name whoever sold it, retired or not; a job cannot be handed
            to somebody who has left. */}
        {people
          .filter((p) => p.active !== false && p.roles?.includes("sales_rep"))
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name}
            </option>
          ))}
      </select>
    </Modal>
  );
}

/**
 * The Verify step.
 *
 * Verifying a lead means getting on the phone and confirming what the intake
 * captured, so this is the intake fields again rather than a checkbox: who they
 * are, how to reach them, where the lead came from and what kind of bid it is.
 * Nothing is written until Submit — closing the box leaves the item pending.
 */
function VerifyModal({
  job,
  onClose,
  onSubmit,
}: {
  job: Job;
  onClose: () => void;
  onSubmit: (values: VerifyValues) => Promise<void>;
}) {
  const { leadSources } = useSession();
  const contact = job.contact ?? {};

  const [firstName, setFirstName] = useState(contact.first_name ?? "");
  const [lastName, setLastName] = useState(contact.last_name ?? "");
  const [company, setCompany] = useState(contact.company ?? "");
  const [cell, setCell] = useState(contact.cell ?? "");
  const [phone, setPhone] = useState(contact.phone ?? "");
  const [phoneLabel, setPhoneLabel] = useState(contact.phone_label ?? "");
  const [email, setEmail] = useState(contact.email ?? "");
  const [leadSource, setLeadSource] = useState(job.lead_source ?? "");
  const [bidType, setBidType] = useState<BidType>(job.bid_type);
  const [claimNumber, setClaimNumber] = useState(job.claim_number ?? "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const named =
    [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") ||
    company.trim();
  const ready = named !== "" && leadSource !== "" && !busy;

  // A source that has since been retired still has to show, or saving would
  // silently change it.
  const sourceOptions =
    job.lead_source && !leadSources.includes(job.lead_source)
      ? [job.lead_source, ...leadSources]
      : leadSources;

  const bidChanged = bidType !== job.bid_type;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    try {
      await onSubmit({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        company: company.trim() || null,
        cell: cell.trim() || null,
        phone: phone.trim() || null,
        phoneLabel: phoneLabel.trim() || null,
        email: email.trim() || null,
        leadSource,
        bidType,
        claimNumber: claimNumber.trim() || null,
        note: note.trim() || null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("Verify")}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
          <button
            className="btn-primary"
            disabled={!ready}
            style={ready ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
            onClick={() => void submit()}
          >
            {busy ? t("Saving…") : t("Submit")}
          </button>
        </>
      }
    >
      {/* Neither name box is required on its own, and marking both with an
          asterisk said they were while Submit happily accepted a first name
          alone. What is actually required is that the customer ends up with a
          name — a person's or a business's — which is the same rule New Lead
          states, so it is stated the same way. */}
      <div className="fgrid one">
        <div className="field">
          <label>{t("First name")}</label>
          <input
            value={firstName}
            autoFocus
            autoComplete="given-name"
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("Last name")}</label>
          <input
            value={lastName}
            autoComplete="family-name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("Company")}</label>
          <input
            value={company}
            autoComplete="organization"
            placeholder={t("Only if the customer is a business")}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
        {named === "" && (
          <div className="na-hint">
            {t(
              "A last name, or a company name — one of them has to be filled in.",
            )}
          </div>
        )}
        {/* Cell first, and the second number asks whose it is — verifying a
            lead is the call where that gets found out. */}
        <div className="field">
          <label>{t("Cell")}</label>
          <PhoneInput placeholder={t("Cell")} value={cell} onChange={setCell} />
        </div>
        <div className="field">
          <label>{t("Alternate phone")}</label>
          <PhoneInput
            placeholder={t("Alternate phone")}
            value={phone}
            onChange={setPhone}
          />
        </div>
        <div className="field">
          <label>{t("Whose is the alternate?")}</label>
          <input
            placeholder={t("Spouse, work, mother")}
            value={phoneLabel}
            onChange={(e) => setPhoneLabel(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("Email")}</label>
          <input
            placeholder={t("Email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label>
            {t("Source")} <span className="req">*</span>
          </label>
          <select
            value={leadSource}
            onChange={(e) => setLeadSource(e.target.value)}
          >
            <option value="">—</option>
            {sourceOptions.map((source) => (
              <option key={source} value={source}>
                {t(source)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            {t("Bid type")} <span className="req">*</span>
          </label>
          <select
            value={bidType}
            onChange={(e) => setBidType(e.target.value as BidType)}
          >
            {(["repair", "retail", "insurance"] as BidType[]).map((b) => (
              <option key={b} value={b}>
                {t("{bid} Bid", { bid: t(capitalise(b)) })}
              </option>
            ))}
          </select>
          {bidChanged && (
            <span className="na-hint">
              {t("Changing the bid type rebuilds this job's checklists.")}
            </span>
          )}
        </div>
        <div className="field">
          <label>{t("Claim number")}</label>
          <input
            placeholder={t("Claim number")}
            value={claimNumber}
            onChange={(e) => setClaimNumber(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("Note")}</label>
          <textarea
            placeholder={t("Note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

export interface SignedContractInput {
  signedOn: string;
  file: File | null;
}

/**
 * Signed Contract.
 *
 * The contract is signed outside the app today, so this is where the signed
 * copy lands. Attaching it is the normal path and is what most reps will do;
 * the step can still be ticked without a file, because a contract signed on
 * paper in a driveway is a real thing and blocking the pipeline on a scan
 * would just get worked around.
 *
 * When e-signature is built it writes the same three columns on the job and
 * this step ticks itself.
 */
function SignedContractModal({
  job,
  onClose,
  onSave,
}: {
  job: Job;
  onClose: () => void;
  onSave: (input: SignedContractInput) => Promise<void>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [signedOn, setSignedOn] = useState(
    job.signed_contract_at ?? new Date().toISOString().slice(0, 10),
  );
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title={t("Signed Contract")}
      subtitle={t(
        "Attach the signed copy. Electronic signing will tick this step on its own once it is built.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onSave({ signedOn, file }).finally(() => setBusy(false));
            }}
          >
            {busy ? t("Saving…") : t("Mark signed")}
          </button>
        </>
      }
    >
      <div className="uploadrow">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        <button
          className="btn-ghost"
          onClick={() => fileInput.current?.click()}
        >
          {file ? t("Choose a different file") : t("Upload signed contract")}
        </button>
        <span className="na-hint">
          {file
            ? file.name
            : job.signed_contract_filename
              ? t("Already attached: {name}", {
                  name: job.signed_contract_filename,
                })
              : t(
                  "PDF or photo. Optional — you can mark it signed without one.",
                )}
        </span>
      </div>

      <div className="fgrid one">
        <div className="field">
          <label>{t("Date signed")}</label>
          <input
            type="date"
            value={signedOn}
            onChange={(e) => setSignedOn(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Unticking Signed Contract: what it takes back, and why, before it does.
 * The database refuses a job sent to production or one with money on it,
 * and its sentence is shown here as it comes.
 */
function UnwindSaleModal({
  onClose,
  onUnwind,
}: {
  onClose: () => void;
  onUnwind: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title="Unwind this sale?"
      subtitle="The job goes back to Proposal Sent and stops counting as signed or sold. The signed contract is marked void and the deposit request is cancelled. The customer is not emailed."
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Keep it signed
          </button>
          <button
            className="btn-primary"
            disabled={busy || reason.trim() === ""}
            onClick={() => {
              setBusy(true);
              setError(null);
              onUnwind(reason.trim())
                .catch((e: unknown) =>
                  setError(e instanceof Error ? e.message : String(e)),
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? "Unwinding…" : "Unwind sale"}
          </button>
        </>
      }
    >
      <div className="fgrid one">
        <div className="field">
          <label htmlFor="unwind-reason">Why</label>
          <input
            id="unwind-reason"
            value={reason}
            placeholder="e.g. Financing not approved"
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <div className="config-warn" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}

interface InspectionValues {
  /** The two slots, already resolved to the columns they are written to. */
  assignees: Record<string, string | null>;
  /** How long, for the end time on the calendar. Never stored. */
  durationMinutes: number;
  iso: string | null;
  notes: string | null;
}

function InspectionModal({
  job,
  onClose,
  onSave,
}: {
  job: Job;
  onClose: () => void;
  onSave: (values: InspectionValues) => Promise<void>;
}) {
  const { people, crews } = useSession();

  /*
   * The inspectors and crews, plus anybody already on this job —
   * leaving them out would blank the box and quietly unassign them on the
   * next save.
   */
  const options = inspectorOptions(
    people,
    crews,
    [
      job.inspector_id,
      job.inspector_crew_id,
      job.inspector_2_id,
      job.inspector_2_crew_id,
    ].filter((id): id is string => Boolean(id)),
  );
  const [draft, setDraft] = useState<InspectionDraft>(() => {
    // Split the stored timestamp into a day and a slot. Typing a time into a
    // datetime field let people book 9:07; the slots keep it to the quarter
    // hour.
    const existing = toLocalDateTimeInput(job.inspection_at);
    const time = existing.slice(11, 16);
    const [first, second] = inspectionSlots(job);
    return {
      ...emptyInspectionDraft(),
      day: existing.slice(0, 10),
      slot: TIME_SLOTS.some((s) => s.value === time) ? time : "",
      one: assigneeInSlot(first, options),
      two: assigneeInSlot(second, options),
      notes: job.inspection_notes ?? "",
    };
  });

  return (
    <Modal
      title={t("Schedule inspection")}
      subtitle={t(
        "Sets the date and time and says who is going. It goes on your Google Calendar and invites them.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            onClick={() =>
              void onSave({
                assignees: inspectionAssigneeColumns(draft),
                iso: inspectionIso(draft),
                durationMinutes: draft.durationMinutes,
                notes: draft.notes || null,
              })
            }
          >
            {t("Save")}
          </button>
        </>
      }
    >
      <InspectionFields draft={draft} options={options} onChange={setDraft} />
    </Modal>
  );
}

/**
 * The handoff email as it went to production (ADR 0304).
 *
 * Read first, because that is what it is for: Lysa looking up what a customer
 * picked. A job handed off before the app kept a copy shows the one in its
 * email history, if the handoff went from the app, and it can be kept on the
 * job from there. One marked sent without a copy, or copied into somebody's
 * own Gmail, has nothing, so the email can be pasted in — and a kept one can
 * be corrected — and the Overview reads the colour out of it either way.
 */
function SendsEmailModal({
  job,
  onClose,
  onSave,
}: {
  job: Job;
  onClose: () => void;
  /** True once it is saved; false when it was not, and the text stays. */
  onSave: (email: string | null) => Promise<boolean>;
}) {
  const saved = job.sends_email ?? "";
  const [history, setHistory] = useState<string | null>(null);
  const [editing, setEditing] = useState(saved === "");
  const [text, setText] = useState(saved);
  const [error, setError] = useState<string | null>(null);
  const typed = useRef(false);

  // Only when nothing was kept: the job's email history, which is where a
  // handoff sent from the app before this landed. The paste box stands until
  // it answers, and stays when it finds nothing or somebody is already typing.
  useEffect(() => {
    if (saved) return;
    let live = true;
    fetchSendsEmail(job)
      .then((found) => {
        if (!live || !found?.trim()) return;
        setHistory(found);
        if (!typed.current) setEditing(false);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [job, saved]);

  const shown = saved || history || "";

  async function save(email: string) {
    setError(null);
    const ok = await onSave(email.trim() === "" ? null : email);
    if (!ok) {
      setError(t("The Sends email was not saved. Try again."));
      return;
    }
    setEditing(false);
    if (email.trim() === "") onClose();
  }

  return (
    <Modal
      title={t("Sends email")}
      subtitle={
        saved
          ? t("The handoff to production, as it went.")
          : history && !editing
            ? t(
                "From the job's email history. No copy was kept on the job yet.",
              )
            : t(
                "No copy was kept of this job's handoff. Paste it here from Gmail so it stays with the job.",
              )
      }
      onClose={onClose}
      actions={
        editing ? (
          <>
            <button
              className="btn-ghost"
              onClick={() => {
                if (!shown) onClose();
                setText(saved);
                setEditing(false);
              }}
            >
              {t("Cancel")}
            </button>
            <button className="btn-primary" onClick={() => void save(text)}>
              {t("Save")}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn-ghost"
              onClick={() => {
                if (!saved) setText(shown);
                setEditing(true);
              }}
            >
              {t("Edit")}
            </button>
            {!saved && history ? (
              <button
                className="btn-primary"
                onClick={() => void save(history)}
              >
                {t("Keep it on the job")}
              </button>
            ) : (
              <button className="btn-primary" onClick={onClose}>
                {t("Done")}
              </button>
            )}
          </>
        )
      }
    >
      {editing ? (
        <textarea
          aria-label={t("Sends email")}
          className="sends-email-text"
          rows={18}
          value={text}
          onChange={(e) => {
            typed.current = true;
            setText(e.target.value);
          }}
        />
      ) : (
        <pre className="sends-email-text">{shown}</pre>
      )}
      {error && <div className="config-warn">{error}</div>}
    </Modal>
  );
}

/** What the rep last left in the email, or null when it was never written. */
function draftBody(draft: { current: EmailDraft } | null): string | null {
  return draft ? draft.current.body : null;
}

function sameDraft(a: EmailDraft, b: EmailDraft): boolean {
  return a.to === b.to && a.subject === b.subject && a.body === b.body;
}

/** Whether the handoff asks about the reminders yet (ADR 0301). */
function asksAboutCheckReminders(
  facts: HandoffFacts | null,
  answers: HandoffAnswers | null,
): boolean {
  return (
    facts !== null &&
    answers !== null &&
    handoffAsksAboutCheckReminders(facts, answers.payMethod)
  );
}

/**
 * Whether the payment reminders wait for a deposit coming by check, asked
 * only when the job does not already know a check is coming (ADR 0301).
 */
function HoldRemindersQuestion({
  asked,
  value,
  onChange,
}: {
  asked: boolean;
  value: "" | "yes" | "no";
  onChange: (value: "" | "yes" | "no") => void;
}) {
  if (!asked) return null;
  return (
    <>
      <label className="na-hint" htmlFor="handoff-hold-reminders">
        {t("Hold the payment reminders while the check is on its way?")}
      </label>
      <select
        id="handoff-hold-reminders"
        value={value}
        onChange={(e) => onChange(e.target.value as "" | "yes" | "no")}
      >
        <option value="">—</option>
        <option value="yes">
          {t("Yes, hold them for {n} days", { n: CHECK_IN_THE_MAIL_DAYS })}
        </option>
        <option value="no">{t("No, keep sending them")}</option>
      </select>
    </>
  );
}

/**
 * The handoff to production.
 *
 * This is the one email in the app that is not to a customer, and the
 * difference matters twice over: no contact flag applies to it, and it is
 * logged as internal so the follow-up clock does not read it as having reached
 * the customer (docs/BUSINESS-RULES.md).
 *
 * Two steps, so the rep only types what the job cannot know (ADR 0280). The
 * first asks those few things — when the crew can start, whether a permit is
 * needed, how the deposit is being paid, what is still undecided, anything
 * else — already answered wherever the job records it. The second is the
 * email, laid out as SENDS and filled in from the job, still editable.
 *
 * "Mark as sent" stays on both, whether or not Gmail is connected. Sending is
 * what moves the job into production, and a rep who has not connected their
 * account yet must still be able to say the handoff went — from their own
 * Gmail, with the copy button, exactly as before.
 *
 * A deposit coming by check that the job does not know about yet also asks
 * whether the payment reminders should wait for it (ADR 0301), and the answer
 * is acted on when the handoff goes, not when it is picked: Cancel changes
 * nothing. Left unanswered, nothing changes either — the handoff is never
 * held up by a question.
 */
function HandoffModal({
  job,
  onClose,
  onCheckComing,
  onSent: onHandedOff,
}: {
  job: Job;
  onClose: () => void;
  onCheckComing?: ((coming: boolean) => Promise<void>) | undefined;
  /** With the email as it went, or null when none was written. */
  onSent: (email: string | null) => Promise<void>;
}) {
  const { people, displayName } = useSession();
  const { account } = useGmailAccount();
  const { senders, own: ownSender } = useGmailSenders();
  const lysa = people.find((p) => p.full_name === "Lysa Sandoval");

  const [facts, setFacts] = useState<HandoffFacts | null>(null);
  const [answers, setAnswers] = useState<HandoffAnswers | null>(null);
  const [writing, setWriting] = useState(false);
  const [holdReminders, setHoldReminders] = useState<"" | "yes" | "no">("");
  /*
   * The email as the rep left it, and the one it was written as. Back closes
   * the email, so without this "Write the email" would build it again from
   * the answers and throw the rep's typing away — and with nothing signed in
   * the app, the selections and what was sold are typed right there.
   */
  const [draft, setDraft] = useState<{
    written: EmailDraft;
    current: EmailDraft;
  } | null>(null);

  useEffect(() => {
    let live = true;
    void fetchHandoffFacts(job, displayName).then((found) => {
      if (!live) return;
      setFacts(found);
      setAnswers(handoffDefaults(found));
    });
    return () => {
      live = false;
    };
  }, [job, displayName]);

  const asksAboutReminders = asksAboutCheckReminders(facts, answers);

  /*
   * The email is kept on the job as it went, so Lysa can open it beside the
   * Sends step and the Overview can read the colour out of it (ADR 0304). Only
   * an email the rep actually opened: one marked sent before it was ever
   * written says nothing the rep looked at, and the handoff may have gone some
   * other way. A check the rep said is coming holds the reminders first
   * (ADR 0301).
   */
  const onSent = async (email: string | null) => {
    if (asksAboutReminders && holdReminders === "yes")
      await onCheckComing?.(true);
    await onHandedOff(email);
  };

  const markSent = (email: string | null) => (
    <button className="btn-ghost" onClick={() => void onSent(email)}>
      {t("Mark as sent")}
    </button>
  );

  /*
   * Write the email, keeping the rep's edits from last time. Built again from
   * the answers only when nothing was edited, or when the answers have changed
   * since and the rep says to — their edits would not say what the answers
   * now do.
   */
  const write = () => {
    if (!facts || !answers) return;
    if (draft) {
      const fresh = { to: lysa?.email ?? "", ...handoffEmail(facts, answers) };
      const edited = !sameDraft(draft.current, draft.written);
      const answersChanged = !sameDraft(fresh, draft.written);
      if (
        !edited ||
        (answersChanged &&
          window.confirm(
            t("Build the email again from your answers?") +
              "\n\n" +
              t("Your changes to the email will be lost."),
          ))
      )
        setDraft(null);
    }
    setWriting(true);
  };

  if (writing && facts && answers) {
    const written = draft?.written ?? {
      to: lysa?.email ?? "",
      ...handoffEmail(facts, answers),
    };
    const opened = draft?.current ?? written;
    const body = opened.body;
    return (
      <ComposeEmail
        title={t("Send handoff to Lysa")}
        subtitle={t("Sending it opens the approved checklist.")}
        to={opened.to}
        subject={opened.subject}
        body={opened.body}
        onDraftChange={(current) => setDraft({ written, current })}
        jobId={job.id}
        internal
        connectedAs={account?.email_address ?? null}
        senders={senders}
        ownSender={ownSender}
        sendLabel={t("Send and mark sent")}
        extraActions={
          <>
            <button className="btn-ghost" onClick={() => setWriting(false)}>
              {t("Back")}
            </button>
            {markSent(body)}
          </>
        }
        onClose={onClose}
        onSent={() => onSent(body)}
      />
    );
  }

  const set = (patch: Partial<HandoffAnswers>) =>
    setAnswers((current) => (current ? { ...current, ...patch } : current));

  return (
    <Modal
      title={t("Hand off to production")}
      subtitle={t(
        "Scope, contract, signed date and deposit come from the job. Check the colour, and answer only what it cannot know.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          {markSent(draftBody(draft))}
          <button className="btn-primary" disabled={!answers} onClick={write}>
            {t("Write the email")}
          </button>
        </>
      }
    >
      {!answers ? (
        <div className="na-hint">{t("Reading the job…")}</div>
      ) : (
        <>
          <HandoffColours
            facts={facts}
            colours={answers.colours ?? []}
            onChange={(colours) => set({ colours })}
          />
          <label className="na-hint" htmlFor="handoff-start">
            {t("When can the crew start?")}
          </label>
          <input
            id="handoff-start"
            value={answers.start}
            placeholder={t("e.g. end of next week")}
            onChange={(e) => set({ start: e.target.value })}
          />
          <label className="na-hint" htmlFor="handoff-permit">
            {t("Permit")}
          </label>
          <select
            id="handoff-permit"
            value={answers.permit}
            onChange={(e) =>
              set({ permit: e.target.value as HandoffAnswers["permit"] })
            }
          >
            <option value="">—</option>
            <option value="yes">{t("Needed")}</option>
            <option value="no">{t("Not needed")}</option>
            <option value="unsure">{t("Not sure")}</option>
          </select>
          <label className="na-hint" htmlFor="handoff-pay">
            {t("How are they paying the deposit?")}
          </label>
          <select
            id="handoff-pay"
            value={answers.payMethod ?? ""}
            onChange={(e) =>
              set({
                payMethod: (e.target.value || null) as HandoffPayMethod | null,
              })
            }
          >
            <option value="">{t("Not decided yet")}</option>
            {HANDOFF_PAY_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {t(method.label)}
              </option>
            ))}
          </select>
          <HoldRemindersQuestion
            asked={asksAboutReminders}
            value={holdReminders}
            onChange={setHoldReminders}
          />
          <label className="na-hint" htmlFor="handoff-undecided">
            {t("Still deciding (one per line)")}
          </label>
          <textarea
            id="handoff-undecided"
            style={{ minHeight: 60 }}
            value={answers.undecided}
            onChange={(e) => set({ undecided: e.target.value })}
          />
          <label className="na-hint" htmlFor="handoff-notes">
            {t("Anything else production should know?")}
          </label>
          <textarea
            id="handoff-notes"
            style={{ minHeight: 90 }}
            value={answers.notes}
            placeholder={t("e.g. Jordan said it's in stock.")}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </>
      )}
    </Modal>
  );
}

const OTHER_COLOUR = "__other__";

/**
 * One colour question per estimate signed, or one for the job when nothing
 * was signed in the app (ADR 0303).
 */
function HandoffColours({
  facts,
  colours,
  onChange,
}: {
  facts: HandoffFacts | null;
  colours: readonly string[];
  onChange: (colours: string[]) => void;
}) {
  const options = facts?.options.length
    ? facts.options
    : [{ name: "", colourOptions: facts?.colourOptions ?? [] }];
  return (
    <>
      {options.map((option, index) => (
        <HandoffColour
          key={index}
          index={index}
          optionName={option.name}
          options={option.colourOptions ?? []}
          value={colours[index] ?? ""}
          onChange={(colour) =>
            onChange(Object.assign([...colours], { [index]: colour }))
          }
        />
      ))}
    </>
  );
}

/**
 * The colour the customer chose, one question per estimate they signed — a
 * roof and its siding are two colours — or one for the job when nothing was
 * signed in the app. Starts on the estimate's colour; the dropdown is what the
 * products come in, and Other… takes anything typed.
 */
function HandoffColour({
  index,
  optionName,
  options,
  value,
  onChange,
}: {
  index: number;
  optionName: string;
  options: readonly string[];
  value: string;
  onChange: (colour: string) => void;
}) {
  const listed = [...new Set([...options, ...(value ? [value] : [])])];
  const [typing, setTyping] = useState(options.length === 0);
  const id = `handoff-colour-${index}`;
  return (
    <>
      <label className="na-hint" htmlFor={id}>
        {optionName
          ? t("Colour the customer chose — {option}", { option: optionName })
          : t("Colour the customer chose")}
      </label>
      {typing ? (
        <input
          id={id}
          value={value}
          placeholder={t("e.g. Weathered Wood")}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <select
          id={id}
          value={value}
          onChange={(e) => {
            if (e.target.value === OTHER_COLOUR) {
              setTyping(true);
              onChange("");
            } else onChange(e.target.value);
          }}
        >
          <option value="">{t("Not chosen yet")}</option>
          {listed.map((colour) => (
            <option key={colour} value={colour}>
              {colour}
            </option>
          ))}
          <option value={OTHER_COLOUR}>{t("Other…")}</option>
        </select>
      )}
    </>
  );
}

/**
 * Which trade is where, under the step it belongs to.
 *
 * This is the visibility the single "All Work" steps would otherwise cost: on a
 * roof + siding + gutters job it says which crew is holding things up, without
 * anybody ticking a third box.
 */
function TradeStrip({
  label,
  orders,
  onOpen,
}: {
  label: string;
  orders: readonly WorkOrderLike[];
  /**
   * Opens a trade's work order, which is where "Mark As Complete" lives.
   *
   * The strip was a read-out until 2026-09-08 and it was the wrong shape for
   * what people do with it: the trade holding the job up is named right here,
   * and the thing you then want is that crew's work order.
   */
  onOpen: (orderId?: string) => void;
}) {
  const rows = tradeProgress(orders);

  if (rows.length === 0) {
    return (
      <div className="tradestrip none">
        {t("No work orders yet — add one and this ticks itself.")}
      </div>
    );
  }

  const wantComplete = label === "All Work Complete";

  return (
    <div className="tradestrip">
      {rows.map((row) => {
        const done = wantComplete
          ? row.state === "complete"
          : row.state !== "not_scheduled";
        return (
          <button
            type="button"
            key={row.trade}
            className={`tp${done ? " on" : ""}`}
            title={
              wantComplete
                ? t("Open the {trade} work order to mark it complete", {
                    trade: row.trade.toLowerCase(),
                  })
                : t("Open the {trade} work order", {
                    trade: row.trade.toLowerCase(),
                  })
            }
            onClick={(e) => {
              // The strip sits inside the step's own click target, which
              // would otherwise fire as well and open the list underneath.
              e.stopPropagation();
              onOpen(row.orderIds.length === 1 ? row.orderIds[0] : undefined);
            }}
          >
            {t(row.trade)}
            {!wantComplete && row.scheduledAt
              ? ` ${fmtDateOnly(row.scheduledAt.slice(0, 10))}`
              : ""}
          </button>
        );
      })}
    </div>
  );
}
